/**
 * Simple, unified status mapping for job statuses
 * Single source of truth for all status-related properties
 */
import type { JobStatus } from "@taxassist/api-client";

// Single configuration object containing all status properties
const STATUS_CONFIG = {
  DRAFT: { display: "Unsubmitted", color: "#E0E0E0" },
  TRIAGE: { display: "Queued", color: "#E1F5FE" },
  NEW_FILES: { display: "New Files", color: "#E0E0E0" },
  SUBMITTED: { display: "In Progress", color: "#FFE0A5" },
  PROCESSING: { display: "In Progress", color: "#FFE0A5" },
  DOWNLOAD_TO_PROCESS: { display: "In Progress", color: "#FFE0A5" },
  VERIFICATION: { display: "In Progress", color: "#FFE0A5" },
  VERIFIED: { display: "In Progress", color: "#FFE0A5" },
  DONE: { display: "Done", color: "#D1F6C7" },
  ERROR: { display: "In Progress", color: "#FFE0A5" }, // Show as In Progress
  CANCELLED: { display: "In Progress", color: "#FFE0A5" }, // Show as In Progress
  ARCHIVED: { display: "Done", color: "#D1F6C7" }, // Show as Done
} as const satisfies Record<JobStatus, { display: string; color: string }>;

// Derived type for user-friendly status values
export type UserFriendlyStatus = (typeof STATUS_CONFIG)[JobStatus]["display"];

// Export for backward compatibility
export type SystemStatus = JobStatus;

/**
 * Get user-friendly display name for a system status
 */
export function getStatusDisplay(
  systemStatus: JobStatus,
): UserFriendlyStatus | null {
  return STATUS_CONFIG[systemStatus]?.display || null;
}

/**
 * Get color for a user-friendly status
 */
export function getStatusColor(userStatus: UserFriendlyStatus): string {
  const entry = Object.values(STATUS_CONFIG).find(
    (config) => config.display === userStatus,
  );
  return entry?.color || "#6b7280";
}

/**
 * Groups jobs by their user-friendly status
 */
export function groupJobsByUserFriendlyStatus<T extends { status: JobStatus }>(
  jobs: T[],
): Record<UserFriendlyStatus, T[]> {
  const displays = Array.from(
    new Set(Object.values(STATUS_CONFIG).map((c) => c.display)),
  ) as UserFriendlyStatus[];
  const groups = Object.fromEntries(
    displays.map((d) => [d, [] as T[]]),
  ) as Record<UserFriendlyStatus, T[]>;

  jobs.forEach((job) => {
    const display = getStatusDisplay(job.status);
    if (display && groups[display]) {
      groups[display].push(job);
    }
  });

  return groups;
}

/**
 * Counts jobs by their user-friendly status
 */
export function countJobsByUserFriendlyStatus(
  jobs: Array<{ status: JobStatus }>,
): Record<UserFriendlyStatus, number> {
  const displays = Array.from(
    new Set(Object.values(STATUS_CONFIG).map((c) => c.display)),
  ) as UserFriendlyStatus[];
  const counts = Object.fromEntries(displays.map((d) => [d, 0])) as Record<
    UserFriendlyStatus,
    number
  >;

  jobs.forEach((job) => {
    const display = getStatusDisplay(job.status);
    if (display && counts.hasOwnProperty(display)) {
      counts[display]++;
    }
  });

  return counts;
}

/**
 * Get all available user-friendly status values
 */
export function getUserFriendlyStatuses(): UserFriendlyStatus[] {
  return Array.from(
    new Set(Object.values(STATUS_CONFIG).map((c) => c.display)),
  ) as UserFriendlyStatus[];
}

/**
 * Convert user-friendly statuses to raw JobStatus values for filtering
 */
export function convertToRawStatuses(
  userFriendlyStatuses: UserFriendlyStatus[],
): JobStatus[] {
  const userStatusSet = new Set(userFriendlyStatuses);

  return (Object.keys(STATUS_CONFIG) as JobStatus[]).filter((rawStatus) =>
    userStatusSet.has(STATUS_CONFIG[rawStatus].display),
  );
}
